// Catalog.ID Client — Entropy Ceremony Derivation
// ============================================================================
// The registration finalize step mints the FINAL password and supersecret via
// a user-visible start/stop ceremony (registration-restructure §4, porting the
// legacy mailsecret SecretKeySetup pattern): the user's click timings and
// pointer movement feed an entropy pool, which is mixed with CSPRNG bytes into
// deterministic material; a counter-expanded byte stream over that material
// drives the SAME rejection samplers as plain generation
// (generatePasswordFrom / generateSupersecretFrom).
//
// Honesty note (mirrored in the ceremony UI copy): this guarantees user
// participation and that the values were not predetermined — it is not a
// cryptographic proof of randomness. Security still rests on the CSPRNG seed;
// the pool can only ADD entropy, never remove it.

import { sha512 } from '@noble/hashes/sha2.js';
import { encodeUtf8 } from './aes';
import { generatePasswordFrom } from './credentials';
import { generateSupersecretFrom } from './supersecret';
import { XRPL_SEED_ENTROPY_LEN } from './xrpl';
import { TOTP_SECRET_LENGTH } from './totp';

const DOMAIN = 'catalog-id ceremony v1';

export interface EntropyPool {
    /** Record one user event, e.g. add('start', performance.now()). */
    add(label: string, value: number): void;
    /** 64-byte digest over every event recorded so far. */
    digest(): Uint8Array;
}

export function createEntropyPool(): EntropyPool {
    const events: string[] = [];
    return {
        add(label: string, value: number): void {
            events.push(`${label}:${value}`);
        },
        digest(): Uint8Array {
            return sha512(encodeUtf8(`${DOMAIN}\n${events.join('|')}`));
        },
    };
}

/** Mix the CSPRNG seed with the user-event pool digest into 64 bytes of
 *  ceremony material. Domain-separated; both inputs bind the output. */
export function deriveCeremonyMaterial(csprngSeed: Uint8Array, poolDigest: Uint8Array): Uint8Array {
    const prefix = encodeUtf8(`${DOMAIN}\nmaterial\n`);
    const input = new Uint8Array(prefix.length + csprngSeed.length + poolDigest.length);
    input.set(prefix, 0);
    input.set(csprngSeed, prefix.length);
    input.set(poolDigest, prefix.length + csprngSeed.length);
    return sha512(input);
}

/**
 * Counter-expanded byte stream over the material: block_i = SHA-512(material ‖
 * u32BE(i)), emitted byte-by-byte. Deterministic — the ceremony draws the
 * password first, then the supersecret, from one stream.
 */
export function createSeededByteStream(material: Uint8Array): () => number {
    let counter = 0;
    let block: Uint8Array = new Uint8Array(0);
    let offset = 0;
    return (): number => {
        if (offset >= block.length) {
            const input = new Uint8Array(material.length + 4);
            input.set(material, 0);
            new DataView(input.buffer).setUint32(material.length, counter++, false);
            block = sha512(input);
            offset = 0;
        }
        return block[offset++];
    };
}

/** Everything one Stop mints (registration-restructure §13.1). */
export interface CeremonyCredentials {
    password: string;
    supersecret: string;
    /** 16 bytes of XRPL family-seed entropy — the `xrpl_master` bag entry's
     *  private material, and what the printed address derives from. */
    xrplSeedEntropy: Uint8Array;
    /** 20 bytes of Authenticator Key (TOTP) secret (§15.1) — printed on its own
     *  sheet as a QR and as 32 base32 characters, and stored on the node at
     *  finalize. Drawn LAST of the personal values. */
    totpSecret: Uint8Array;
    /** Org registration only: the organization's supersecret. */
    orgSupersecret?: string;
    /** The compact bitcash wallet key of a PERSONAL or delegate ceremony
     *  (registration-wallets §4.9) — absent when no minter was injected, and on
     *  the organization path, which draws the two keys below instead. */
    bitcashWalletKey?: string;
    /** Org ceremony: the FOUNDING DELEGATE's wallet, drawn FIRST of the two
     *  (§4.13). It lands in the delegate's bag. */
    founderBitcashWalletKey?: string;
    /** Org ceremony: the ORGANIZATION's wallet, drawn SECOND. It lands in the
     *  org bag and pays for organization work (D14). */
    orgBitcashWalletKey?: string;
}

/** What one Stop is asked to draw. */
export interface CeremonyDrawOptions {
    /** Organization registration: draw the organization's supersecret too — and,
     *  with a minter, the PAIR of wallets instead of the single personal one. */
    includeOrgSupersecret?: boolean;
    /**
     * Compose ONE compact bitcash wallet key out of this ceremony's own byte
     * stream, consuming 18 sampled characters after a routing prefix and 22
     * without one:
     *
     *     next => generateWalletKeyFrom(next, routingPrefix, TYPE_CASH)
     *
     * INJECTED, not imported: an import of `crypto/bitcash.ts` from this module
     * would close the §13.1 cycle. The caller (a wizard) knows the routing
     * prefix (the routing seam, docs/cid-governance.md M13), which is the only
     * thing the composition needs beyond the stream.
     *
     * Absent ⇒ NO wallet is drawn and not one byte of the stream is consumed,
     * so a resume from a pre-round-10 stash reproduces its values exactly.
     */
    mintWalletKey?: (nextByte: () => number) => string;
}

/**
 * Draw one ceremony's values from `material`, in the ONE order the UI displays
 * them and the wizards consume them (§13.1):
 *
 *   password → supersecret → 16 XRPL seed bytes → org supersecret (when asked)
 *   → 20 TOTP secret bytes → the bitcash wallet key(s) (when a minter is given:
 *   one on the personal path; the FOUNDER's and then the ORGANIZATION's on the
 *   org path, §4.13 — that order, because swapping them hands each account the
 *   other's money)
 *
 * Every round appends its new value at the very END of the stream, so the
 * values that already existed stay byte-identical and the golden vector only
 * ever GAINS a field. Round 8's TOTP secret therefore sits AFTER the org
 * supersecret (§15.1 item 6) — which does mean the personal and org ceremonies
 * draw DIFFERENT Authenticator Keys from the same material, and that is fine:
 * the org account gets no key at all and the founding delegate's is minted by
 * the org ceremony. Deterministic in `material` — the golden vectors in
 * entropy.test.ts pin exactly this order.
 */
export function drawCeremonyCredentials(
    material: Uint8Array,
    options: CeremonyDrawOptions = {},
): CeremonyCredentials {
    const next = createSeededByteStream(material);
    const password = generatePasswordFrom(next);
    const supersecret = generateSupersecretFrom(next);
    const xrplSeedEntropy = new Uint8Array(XRPL_SEED_ENTROPY_LEN);
    for (let i = 0; i < xrplSeedEntropy.length; i++) xrplSeedEntropy[i] = next();
    const orgSupersecret = options.includeOrgSupersecret ? generateSupersecretFrom(next) : undefined;
    const totpSecret = new Uint8Array(TOTP_SECRET_LENGTH);
    for (let i = 0; i < totpSecret.length; i++) totpSecret[i] = next();

    // Round 10's draw, at the very END of the stream. The org ceremony takes
    // TWO keys and the founder's comes first (§4.13).
    const mint = options.mintWalletKey;
    const wallets: Pick<CeremonyCredentials,
        'bitcashWalletKey' | 'founderBitcashWalletKey' | 'orgBitcashWalletKey'> = {};
    if (mint) {
        if (options.includeOrgSupersecret) {
            wallets.founderBitcashWalletKey = mint(next);
            wallets.orgBitcashWalletKey = mint(next);
        } else {
            wallets.bitcashWalletKey = mint(next);
        }
    }

    return {
        password,
        supersecret,
        xrplSeedEntropy,
        totpSecret,
        ...(orgSupersecret !== undefined ? { orgSupersecret } : {}),
        ...wallets,
    };
}
